import { addMessageAdvanced } from "~/features/toasts/redux/actions";
import { CART_PNA_THRESHOLD } from "~/utils/constants";
import { selectLineItemsByProduct } from "~/features/cart/cart/redux/selectors/cartSelectors";
import { orchestratePnARefresh } from "~/features/cart/cart/redux/actions/cartRefresh";
import { cartLoadErrorAction, cartLoadFinishAction, cartLoadStartAction } from "~/features/cart/cart/redux/actions";
import { getData } from "~/features/cart/cart/api/CartView";
import { type ThunkAction } from "redux-thunk";
import { type RootState } from "~/app/store";
import { type AnyAction } from "~/app/store/types";
import { userIsCustomer } from "~/domains/user/utils/roles";
import { type TransformedCartData } from "~/features/cart/cart/transformers/CartResponseTransformer";
import { trackCartContents } from "~/app/analytics/ga/abandonment";
import { TranslationType } from "~/common/types/TranslationModel";
import { requestBulkPriceData, requestBulkAvailabilityData } from "~/domains/product/redux/actions";
import type { BulkPriceRequest, BulkAvailabilityRequest } from "~/domains/product/redux/actions";
import { PackageEntrySource } from "~/features/cart/cart/models/CartData";
import * as pnaUpdateAccumulator from "./pnaUpdateAccumulator";

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;
type AsyncThunkResult<R> = ThunkAction<Promise<R>, RootState, undefined, AnyAction>;
function showAboveThresholdMessageAction() {
  return addMessageAdvanced("info", {
    value: "page.cart.message.abovePnaThreshold",
    type: TranslationType.INTERNAL,
    params: { count: CART_PNA_THRESHOLD },
  });
}

function showBulkUploadSuccessMessageAction() {
  const params = new URLSearchParams(window.location.search);
  const count = +params.get("bulkSuccess")!;
  return addMessageAdvanced("info", {
    value: "cartPage.msg.successfullyAdded",
    type: TranslationType.INTERNAL,
    params: { count },
  });
}

function loadCartDataThunk(): AsyncThunkResult<TransformedCartData> {
  return async (dispatch) => {
    dispatch(cartLoadStartAction());

    try {
      const cartData = await getData();
      dispatch(cartLoadFinishAction(cartData));
      return cartData;
    } catch (e) {
      console.error(e);
      dispatch(cartLoadErrorAction());
      throw e;
    }
  };
}

export function cartPageLoadThunk(): ThunkResult<void> {
  return async (dispatch, getState) => {
    const data = await dispatch(loadCartDataThunk());

    // Products are loaded into the product reducer via the CART_LOAD_SUCCESS action.
    // await Promise.all(
    //   data.productCodes.map((code) =>
    //     dispatch(requestFullProductData(code, "", { fetchPrice: false, fetchAvailability: false })),
    //   ),
    // );

    if (userIsCustomer()) {
      const params = new URLSearchParams(window.location.search);
      if (params.has("bulkSuccess")) {
        dispatch(showBulkUploadSuccessMessageAction());
      }

      if (Object.values(data.packageEntriesById).length <= CART_PNA_THRESHOLD) {
        dispatch(performCartRefresh());
      } else {
        dispatch(showAboveThresholdMessageAction());
      }

      trackCartContents(getState());
    }
  };
}

function performCartRefresh(): ThunkResult<void> {
  return async (dispatch, getState) => {
    const lineItemsByProduct = selectLineItemsByProduct(getState());
    const entries = Object.values(lineItemsByProduct);
    entries.sort((a, b) => (a[0].entryNumber ?? 0) - (b[0].entryNumber ?? 0));

    const priceRequests: BulkPriceRequest[] = [];
    const availabilityRequests: BulkAvailabilityRequest[] = [];

    for (const lineItems of entries) {
      const productCode = lineItems[0].productCode;
      const priceReferences = new Set<string>();
      let packCode: string | undefined;

      for (const li of lineItems) {
        priceReferences.add(li.priceReference);
        // Take the first QUOTE packCode — matches orchestratePnARefreshRegular which uses allLineItems[0]
        if (!packCode && li.source === PackageEntrySource.QUOTE && li.packCode) {
          packCode = li.packCode;
        }
      }

      for (const priceReference of priceReferences) {
        priceRequests.push({ productCode, priceReference, packCode });
      }
      availabilityRequests.push({ productCode, packCode });
    }

    // Fire bulk fetches without awaiting — the bulk actions register per-key deferred
    // promises in pPromisesInFlight/aPromisesInFlight so that orchestratePnARefresh's
    // single-product thunks attach to those in-flight promises rather than triggering
    // duplicate requests. Each product line updates progressively as its fetch resolves.
    dispatch(requestBulkPriceData(priceRequests));
    dispatch(requestBulkAvailabilityData(availabilityRequests));

    const gen = pnaUpdateAccumulator.activate(dispatch);
    entries.forEach((lineItems) => dispatch(orchestratePnARefresh(lineItems, undefined, gen)));
  };
}
